import * as React from 'react';
import {
  ArrowSquareOut,
  ArrowsClockwise,
  CaretLeft,
  Check,
  CircleNotch,
  GitPullRequest,
  LockKey,
  Sparkle,
} from '@phosphor-icons/react';
import { Button, Input, Tabs } from '@fragments-sdk/ui';
import type { FindingsPrView } from '../findings-url';
import { plural } from '../findings-display-model';
import type {
  IndexedPullRequest,
  PrActionState,
  PrDecisionDetail,
  PrDecisionRow,
} from '../findings-types';
import {
  getPrMergeGate,
  getPrReviewCounts,
  normalizePrReviewView,
  PR_REVIEW_TABS,
} from './pr-workbench-model';
import { PrDecisionCard } from './pr-decision-card';
import type { PlanId } from '../../../../../convex/billing/plans';
import s from '../findings.module.scss';

export function PrDetailWorkbench({
  prNumber,
  repoFullName,
  pullRequest,
  summary,
  decisions,
  selectedDecisionKey,
  detail,
  activePrView,
  searchQuery,
  plan,
  readOnly,
  autofixUsage,
  actionState,
  onBack,
  onViewChange,
  onSearchChange,
  onSelectDecision,
  onCreateFix,
  onPostComments,
  onApprove,
  onRequestFix,
  onRerun,
}: {
  prNumber: number;
  repoFullName?: string;
  pullRequest?: IndexedPullRequest;
  summary: {
    prNumber: number;
    branch?: string;
    repoFullName?: string;
    prAuthor?: string;
    latestRunAt: number;
    filteredDecisionCount: number;
    totals: {
      findings: number;
      introduced: number;
      existing: number;
      safeFixes: number;
      policyNeeded: number;
      manualReview: number;
      blocking?: number;
      advisory?: number;
      passing?: number;
    };
  };
  decisions: PrDecisionRow[];
  selectedDecisionKey: string | null;
  detail?: PrDecisionDetail | null;
  activePrView: FindingsPrView;
  searchQuery: string;
  plan: PlanId;
  readOnly: boolean;
  autofixUsage: { current: number; limit: number };
  actionState: PrActionState;
  onBack: () => void;
  onViewChange: (view: FindingsPrView) => void;
  onSearchChange: (value: string) => void;
  onSelectDecision: (key: string) => void;
  onCreateFix: (detail: PrDecisionDetail) => void;
  onPostComments: (detail: PrDecisionDetail) => void;
  onApprove: (detail: PrDecisionDetail) => void;
  onRequestFix: (detail: PrDecisionDetail) => void;
  onRerun: () => void;
}) {
  const normalizedView = normalizePrReviewView(activePrView);
  const reviewCounts = getPrReviewCounts(summary);
  const gate = getPrMergeGate(summary);
  const githubUrl =
    repoFullName && prNumber
      ? `https://github.com/${repoFullName}/pull/${prNumber}`
      : pullRequest?.url;

  return (
    <section className={s.prWorkbench} aria-label={`PR #${prNumber} findings`}>
      <div className={s.prWorkbenchCrumb}>
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          <CaretLeft size={13} />
          Pull requests
        </Button>
      </div>

      <header className={s.prWorkbenchHeader}>
        <div className={s.prWorkbenchTitleBlock}>
          <span className={s.prEyebrow}>#{prNumber}</span>
          <h1>{pullRequest?.title ?? `Pull request #${prNumber}`}</h1>
          <p>
            <span className={s.prBranchPill}>
              <GitPullRequest size={13} />
              {summary.branch ?? pullRequest?.branch ?? 'unknown branch'}
            </span>
            <span>{summary.prAuthor ?? pullRequest?.author ?? 'unknown'}</span>
            <span>{repoFullName ?? summary.repoFullName}</span>
          </p>
        </div>
        <div className={s.prWorkbenchHeaderActions}>
          <Button
            as="a"
            variant="outlined"
            size="sm"
            href={githubUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!githubUrl}
          >
            <ArrowSquareOut size={14} />
            GitHub
          </Button>
          <Button type="button" variant="outlined" size="sm" onClick={onRerun}>
            <ArrowsClockwise size={14} />
            Re-run
          </Button>
        </div>
      </header>

      <div className={s.prMergeGate} data-state={gate.state}>
        <span className={s.prMergeGateIcon}>
          {gate.state === 'blocked' ? (
            <LockKey size={18} />
          ) : (
            <Check size={18} />
          )}
        </span>
        <span className={s.prMergeGateText}>
          <strong>{gate.title}</strong>
          <span>{gate.meta}</span>
        </span>
        <Button type="button" variant="outlined" size="sm" disabled>
          {gate.state === 'blocked' ? (
            <LockKey size={14} />
          ) : (
            <Check size={14} />
          )}
          Merge
        </Button>
      </div>

      <div className={s.prWorkbenchToolbar}>
        <Tabs
          value={normalizedView}
          onValueChange={(next) => onViewChange(next as FindingsPrView)}
          variant="pills"
        >
          <Tabs.List className={s.prReviewTabs}>
            {PR_REVIEW_TABS.map((tab) => (
              <Tabs.Tab key={tab.value} value={tab.value}>
                {tab.label}
                <span>{reviewCounts[tab.countKey]}</span>
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
        <Input
          className={s.prSearch}
          type="text"
          size="sm"
          placeholder="Search PR findings..."
          value={searchQuery}
          onValueChange={onSearchChange}
          aria-label="Search PR findings"
          withFieldWrapper={false}
        />
      </div>

      {actionState.status !== 'idle' && (
        <div
          className={s.prActionStatus}
          data-tone={actionState.status}
          role={actionState.status === 'error' ? 'alert' : 'status'}
        >
          {actionState.status === 'running' ? (
            <>
              <CircleNotch size={13} className={s.spinIcon} />
              {actionState.action === 'autofix'
                ? 'Creating fix PR...'
                : actionState.action === 'comments'
                  ? 'Posting PR comments...'
                  : 'Saving reviewer decision...'}
            </>
          ) : actionState.status === 'success' ? (
            <>
              <span>{actionState.message}</span>
              {actionState.prUrl && (
                <Button
                  as="a"
                  href={actionState.prUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="link"
                  size="sm"
                >
                  Open PR
                </Button>
              )}
            </>
          ) : (
            actionState.message
          )}
        </div>
      )}

      <div className={s.prDecisionList}>
        {decisions.length === 0 ? (
          <div className={s.prInboxEmpty}>
            <Check size={28} />
            <strong>
              {normalizedView === 'passing'
                ? 'No passing check records yet'
                : 'No matching PR findings'}
            </strong>
            <span>
              {normalizedView === 'passing'
                ? 'This scan stores failing findings and reviewer decisions; passing rule rows are not recorded yet.'
                : 'Try another tab or search term.'}
            </span>
          </div>
        ) : (
          decisions.map((decision) => (
            <PrDecisionCard
              key={decision.key}
              decision={decision}
              detail={selectedDecisionKey === decision.key ? detail : undefined}
              selected={selectedDecisionKey === decision.key}
              loadingDetail={
                selectedDecisionKey === decision.key && detail === undefined
              }
              plan={plan}
              readOnly={readOnly}
              quotaReached={
                Number.isFinite(autofixUsage.limit) &&
                autofixUsage.current >= autofixUsage.limit
              }
              actionState={actionState}
              onSelect={() => onSelectDecision(decision.key)}
              onCreateFix={onCreateFix}
              onPostComments={onPostComments}
              onApprove={onApprove}
              onRequestFix={onRequestFix}
            />
          ))
        )}
      </div>

      <footer className={s.prWorkbenchFooter}>
        <Sparkle size={14} />
        <span>
          Checked by Fragments · {plural(summary.totals.findings, 'finding')} ·
          agent context via MCP
        </span>
      </footer>
    </section>
  );
}
